import { combineReducers } from 'redux';
import { RELEASE_SCOPE, releaseNotesConstants as C } from '../constants/releaseNotes.constants';

const listInitialState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  requestId: null
};

// One list slice per page, so the reader's and the manage page's lists can
// never overwrite each other. A response for anything but the newest request
// is dropped.
const createListReducer = ([requestType, successType, failureType]) => (state = listInitialState, action) => {
  switch (action.type) {
    case requestType:
      return { ...state, loading: true, error: null, requestId: action.requestId };
    case successType:
      if (action.requestId !== state.requestId) return state;
      return {
        ...state,
        items: (action.data && action.data.items) || [],
        loaded: true,
        loading: false,
        error: null
      };
    case failureType:
      if (action.requestId !== state.requestId) return state;
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
};

const removeNote = (state, action) => ({
  ...state,
  items: state.items.filter(item => item.id !== action.noteId)
});

const manageListReducer = createListReducer([
  C.GET_MANAGE_NOTES_REQUEST, C.GET_MANAGE_NOTES_SUCCESS, C.GET_MANAGE_NOTES_FAILURE
]);

export const reader = createListReducer([
  C.GET_READER_NOTES_REQUEST, C.GET_READER_NOTES_SUCCESS, C.GET_READER_NOTES_FAILURE
]);

const petasenseListReducer = createListReducer([
  C.GET_PETASENSE_NOTES_REQUEST, C.GET_PETASENSE_NOTES_SUCCESS, C.GET_PETASENSE_NOTES_FAILURE
]);

// Petasense's notes on a partner admin's From Petasense tab. A decision made
// shows at once on its note; the list fetched after it confirms it.
export const petasense = (state = listInitialState, action) => {
  if (action.type === C.DECIDE_PETASENSE_NOTE_SUCCESS && action.data) {
    return { ...state, items: state.items.map(item => (item.id === action.noteId ? action.data : item)) };
  }
  return petasenseListReducer(state, action);
};

export const manage = (state = listInitialState, action) => {
  if (action.type === C.DELETE_NOTE_SUCCESS) {
    return removeNote(state, action);
  }
  return manageListReducer(state, action);
};

const releaseListReducer = createListReducer([
  C.GET_RELEASES_REQUEST, C.GET_RELEASES_SUCCESS, C.GET_RELEASES_FAILURE
]);

// Only the release actions for this scope reach its list, so the reader's
// live releases and the manage page's full list never stand in for each other.
const IGNORED = { type: '' };
const scopedReleases = scope => (state, action) => (
  releaseListReducer(state, action.scope === scope ? action : IGNORED)
);

export const releases = combineReducers({
  [RELEASE_SCOPE.LIVE]: scopedReleases(RELEASE_SCOPE.LIVE),
  [RELEASE_SCOPE.ALL]: scopedReleases(RELEASE_SCOPE.ALL)
});

const currentInitialState = {
  note: null,
  loading: false,
  notFound: false,
  failed: false,
  requestId: null
};

const NOT_FOUND = 404;

// The note the editor is working on, loaded on its own so a deep link to the
// editor never depends on a list some other page fetched.
export const current = (state = currentInitialState, action) => {
  switch (action.type) {
    case C.GET_NOTE_REQUEST:
      return { ...currentInitialState, loading: true, requestId: action.requestId };
    case C.GET_NOTE_SUCCESS:
      if (action.requestId !== state.requestId) return state;
      return { ...state, note: action.data, loading: false };
    case C.GET_NOTE_FAILURE:
      if (action.requestId !== state.requestId) return state;
      // Only a 404 means the note is gone; anything else may work on a retry.
      return {
        ...state, loading: false, notFound: action.status === NOT_FOUND, failed: action.status !== NOT_FOUND
      };
    case C.SAVE_NOTE_SUCCESS:
      return { ...state, note: action.data };
    case C.CLEAR_NOTE:
      return currentInitialState;
    default:
      return state;
  }
};

// seenAt is when the user last read the notes: a note that went live after it
// is unread, which the bell marks on its rows.
const unreadInitialState = {
  count: 0, seenAt: null, loaded: false, seenRequestId: 0, seenPending: false
};

// A count asked for before the user marked notes seen is out of date when it
// lands (the reader marks them seen as it opens, while the bell is still
// asking), so it is dropped. So is one that lands while the mark is still on
// its way: the server may have counted before recording it. A failed mark
// fetches the count again (see markSeen).
export const unread = (state = unreadInitialState, action) => {
  switch (action.type) {
    case C.GET_UNREAD_COUNT_SUCCESS:
      if (action.requestId < state.seenRequestId || state.seenPending) return { ...state, loaded: true };
      return {
        ...state,
        count: (action.data && action.data.count) || 0,
        seenAt: (action.data && action.data.seen_at) || null,
        loaded: true
      };
    case C.MARK_SEEN_REQUEST:
      return {
        ...state, count: 0, loaded: true, seenRequestId: action.requestId, seenPending: true
      };
    case C.MARK_SEEN_SUCCESS:
    case C.MARK_SEEN_FAILURE:
      // Only the latest mark settles it; an earlier one may land first.
      return action.requestId === state.seenRequestId ? { ...state, seenPending: false } : state;
    default:
      return state;
  }
};

export const releaseNotes = combineReducers({
  reader,
  manage,
  releases,
  current,
  unread,
  petasense
});
